import type {
  DeliverableDto,
  DeliverablesResponse,
  HomeResponse,
  JourneyMode,
  MilestoneDto,
  ResourceDto,
  SalesStepDto,
  SectionKey,
  SectionState
} from "../shared/dto";
import {
  appQuestionnaireState,
  appReleaseState,
  deliverableState,
  gatedDeliverableState,
  materialsApproved,
  newest,
  publicationState,
  salespageStepState
} from "./rail-fallback";

// F171 split (growth tripwire): the fallback twins and the ?test=1 walkthrough
// live in their own modules; this file re-exports what callers import from it.
export { salespagePublished, salespageTakeawayReached } from "./rail-fallback";
export { simulateClientSnapshot, simulateRailSteps, surfaceStateForStep } from "./rail-simulation";
import { clientSectionOpen } from "../shared/client-step-access";
import {
  CLIENT_NOTIFICATION_SURFACES,
  type ClientNotificationSurface
} from "../shared/notification-targets";
import {
  SUBSTEP_RAIL_KEYS,
  railSubstepMarked,
  type RailStepProgress,
  type RailStepProgressMap,
  type SubstepRailKey
} from "../shared/rail-substeps";

export const RAIL_STEP_KEYS = [
  "onboarding_call",
  "profile",
  "apple",
  "q1",
  "knowhow",
  "training",
  "beta",
  "feedback",
  "marketing_call",
  "q2",
  "strategy",
  "salespage",
  "payments",
  "emails",
  "webinar",
  "stripe",
  "app_questionnaire",
  "salespage_publish",
  "handover",
  "app_release"
] as const;
export const RAIL_STEP_COUNT = RAIL_STEP_KEYS.length;

export type RailStepKey = (typeof RAIL_STEP_KEYS)[number];
const APP_RAIL_STEP_KEYS = new Set<RailStepKey>(["apple", "app_questionnaire", "app_release"]);
// F82: the Stripe step is hidden from the client journey (data model stays)
// and the publication became phase 3 of the sales page step. Both keys keep
// their META, labels and simulation states; only the JOURNEY omits them:
// 19 -> 17 steps with the app, 16 -> 14 without it.
const HIDDEN_RAIL_STEP_KEYS = new Set<RailStepKey>(["stripe", "salespage_publish"]);
// F143 (Pavel 2026-08-13): the whole of chapter 1. A `sales_only` client had
// their Buddy built outside the system, so these steps are not "done" for them
// and not "skipped" - they never existed.
const BUDDY_RAIL_STEP_KEYS = new Set<RailStepKey>([
  "onboarding_call", "profile", "apple", "q1", "knowhow", "training", "beta", "feedback"
]);
// 🔴 F152-W2 (Pavel 2026-08-14), verbatim: "Pod marketingovým videem by mělo
// být: uprav, doplň svůj profil. Měl by to být druhý krok ještě před
// dotazníkem. Ať už ti lidé budou importovaní, nebo si to koupí, ten profil by
// se stejně měl udělat." So `profile` is the ONE step of chapter 1 a sales_only
// client keeps - the strategy and the sales page are written FROM it - and it
// does not stay where it stands in the full journey: it moves behind the
// marketing video.
const SALES_ONLY_KEPT_BUDDY_STEP: RailStepKey = "profile";
// 🔴 F174 (David Riha 2026-10-20): "Marie Diamond v launchpadu teď nemá sekci
// pro apple developer account protože se jí úplně skryla sekce pro stavění
// instance." A sales_only client who BOUGHT the app still has to hand over
// their Apple Developer account, and `app_questionnaire` only opens on the
// approved sales page - so without this step they had nowhere to enter it.
// `apple` therefore stays in the sales_only journey (app clients only, the
// app axis filters it like everywhere else), right behind the profile.
const SALES_ONLY_KEPT_APP_STEP: RailStepKey = "apple";
// 🔴 The handover goes with them, and it is the ONE that cannot be solved by
// data. `buildSalesSteps` DERIVES the step open the moment all four materials
// are approved (F101), so a sales_only client who reaches the finish line
// would open a seventh step nobody can close: S25 refuses `handover` as a
// `protected_completion` and S20 demands a Telegram id and a confirmed
// dashboard the client never had. The only answer is that the step is not in
// the journey. `app_release` follows it, because its own state reads
// `handoverState !== "done"` - left in, it would be a permanently locked step
// at the end of the rail with no way to finish.
const SALES_ONLY_HIDDEN_RAIL_STEP_KEYS = new Set<RailStepKey>(["handover", "app_release"]);
// 🔴 F171 (Tomas Pohl 2026-10-06): "Napojení plateb" exists only for a client
// who bought the payment setup, on EITHER journey. It is not an axis of
// `railJourneyKeys` (that is the static journey both rails and the substep map
// share); the SERVER decides it, by emitting a `payments` sales step
// (worker/src/sales-steps.ts), and `railJourneyKeysFor` inserts it after the
// sales page. One truth, carried by the payload both rails already read, so the
// client rail and the partner rail cannot disagree about it.
const CONDITIONAL_RAIL_STEP_KEYS = new Set<RailStepKey>(["payments"]);
const PAYMENTS_AFTER: RailStepKey = "salespage";

/**
 * The axes that decide WHICH steps a client walks. An object rather than a
 * second boolean on purpose: three positional booleans is how
 * `railJourneyKeys(true, false, true)` gets written, and the object makes the
 * compiler name every call site when an axis is added.
 */
export interface JourneyAxes {
  includesApp: boolean;
  journeyMode: JourneyMode;
}

export function journeyAxesOf(client: HomeResponse["client"]): JourneyAxes {
  return { includesApp: client.includesApp, journeyMode: client.journeyMode };
}

/**
 * 🔴 F152-W2 (D9): the sales_only journey is an ORDER, not a filter over the
 * full one. `RAIL_STEP_KEYS` puts `profile` at index 1, before chapter 2 even
 * starts, so merely keeping it out of `BUDDY_RAIL_STEP_KEYS` would have drawn
 * Profil BEFORE the marketing video - the opposite of the decision. A filter
 * can only ever remove; naming the sequence is the only way to move a step.
 *
 * The SET is still derived truth, not a second opinion: the guard test holds
 * this list against `RAIL_STEP_KEYS` minus the hidden keys, minus chapter 1
 * except `SALES_ONLY_KEPT_BUDDY_STEP`, minus the two steps this journey cannot
 * close - so a step added to the catalog tomorrow fails here by name instead of
 * disappearing quietly from one of the two journeys.
 *
 * `app_questionnaire` keeps the tail it has today (the app axis is independent
 * of the journey, F143), so the app-free client walks seven steps and the one
 * who bought the app walks eight.
 */
const SALES_ONLY_RAIL_ORDER: readonly RailStepKey[] = [
  "marketing_call",
  "profile",
  "apple",
  "q2",
  "strategy",
  "salespage",
  "emails",
  "webinar",
  "app_questionnaire"
];

/**
 * 🔴 F152-W2: is this step drawn for ANYBODY?
 *
 * The difference matters exactly once, and it is a trap: `stripe` and the
 * publication are hidden from EVERY client's rail (F82) and yet their SURFACES
 * are live pages any client can be sent to - the publication is phase 3 of the
 * sales page step and a `section_unlocked` notification can name the stripe
 * section. So "not in this client's rail" is not the same question as "not this
 * client's step", and only the second one may be refused.
 */
export function railStepEverDrawn(key: RailStepKey): boolean {
  return !HIDDEN_RAIL_STEP_KEYS.has(key);
}

/** F171: the server emitted the payments step for this client. */
export function hasPaymentsStep(salesSteps: ReadonlyArray<SalesStepDto> | null | undefined): boolean {
  return Boolean(salesSteps?.some((step) => step.key === "payments"));
}

/** The set `SALES_ONLY_RAIL_ORDER` must cover, for the guard test to hold. */
export function salesOnlyRailMembership(key: RailStepKey): boolean {
  if (HIDDEN_RAIL_STEP_KEYS.has(key)) return false;
  if (SALES_ONLY_HIDDEN_RAIL_STEP_KEYS.has(key)) return false;
  if (CONDITIONAL_RAIL_STEP_KEYS.has(key)) return false;
  if (key === SALES_ONLY_KEPT_BUDDY_STEP || key === SALES_ONLY_KEPT_APP_STEP) return true;
  return !BUDDY_RAIL_STEP_KEYS.has(key);
}

export function railJourneyKeys(axes: JourneyAxes): RailStepKey[] {
  const appOpen = (key: RailStepKey) => axes.includesApp || !APP_RAIL_STEP_KEYS.has(key);
  if (axes.journeyMode === "sales_only") return SALES_ONLY_RAIL_ORDER.filter(appOpen);
  return RAIL_STEP_KEYS.filter((key) =>
    !HIDDEN_RAIL_STEP_KEYS.has(key) && !CONDITIONAL_RAIL_STEP_KEYS.has(key) && appOpen(key));
}

/**
 * F171: THIS client's journey = the static one plus the payments step when the
 * server emitted it, placed right after the sales page on both journeys.
 */
export function railJourneyKeysFor(
  axes: JourneyAxes,
  salesSteps: ReadonlyArray<SalesStepDto> | null | undefined
): RailStepKey[] {
  const keys = railJourneyKeys(axes);
  if (!hasPaymentsStep(salesSteps)) return keys;
  const at = keys.indexOf(PAYMENTS_AFTER);
  return at < 0 ? [...keys, "payments"] : [...keys.slice(0, at + 1), "payments", ...keys.slice(at + 1)];
}

export type RailChapter = "buddy" | "sales";
export type RailDisplayStatus =
  | "locked"
  | "on_deck"
  | "review"
  | "waiting_apple"
  | "working"
  | "done"
  | "open"
  | "testing"
  | "feedback"
  | "client";
export const STEP_HEADING_KEYS = Object.fromEntries(
  RAIL_STEP_KEYS.map((key) => [key, `rail.steps.${key}`])
) as Record<RailStepKey, string>;
export type RailTarget =
  | "home"
  | "onboarding_call"
  | "marketing_call"
  | "profile"
  | "app"
  | "app:apple"
  | "q1"
  | "q2"
  | "knowhow"
  | "training"
  | "beta"
  | "beta:feedback"
  | "sales"
  | "strategy"
  | "salespage"
  | "emails"
  | "webinar"
  | "publish"
  | "stripe"
  | "payments"
  | "handover"
  | "app:questionnaire"
  | "app:release";

export interface RailStep {
  key: RailStepKey;
  labelKey: string;
  chapter: RailChapter;
  state: SectionState;
  displayStatus: RailDisplayStatus;
  primaryNext: boolean;
  backingSection: SectionKey;
  target: RailTarget;
  trainingPhotoPulse: boolean;
  profilePhotoAvailable: boolean;
  /**
   * F123: how far the client is INSIDE this step, when the step contains
   * steps. Server-derived (shared/rail-substeps.ts); null for the fifteen
   * steps that are a single act, and for any payload that predates F123.
   */
  progress?: RailStepProgress | null;
}

export interface ClientSimulationSnapshot {
  home: HomeResponse;
  delivery: DeliverablesResponse;
  resources: ResourceDto[];
  milestones: MilestoneDto[];
}

export const SALES_STEP_TARGETS = {
  strategy: "strategy",
  salespage: "salespage",
  emails: "emails",
  webinar: "webinar",
  stripe: "stripe",
  payments: "payments",
  app_questionnaire: "app:questionnaire",
  publish: "publish",
  handover: "handover",
  release: "app:release"
} as const satisfies Record<SalesStepDto["key"], RailTarget>;

const META: Record<RailStepKey, Pick<RailStep, "labelKey" | "chapter" | "backingSection" | "target">> = {
  onboarding_call: { labelKey: "rail.labels.onboarding_call", chapter: "buddy", backingSection: "onboarding_call", target: "onboarding_call" },
  profile: { labelKey: "rail.labels.profile", chapter: "buddy", backingSection: "q1", target: "profile" },
  apple: { labelKey: "rail.labels.apple", chapter: "buddy", backingSection: "app", target: "app:apple" },
  q1: { labelKey: "rail.labels.q1", chapter: "buddy", backingSection: "q1", target: "q1" },
  knowhow: { labelKey: "rail.labels.knowhow", chapter: "buddy", backingSection: "knowhow", target: "knowhow" },
  training: { labelKey: "rail.labels.training", chapter: "buddy", backingSection: "training", target: "training" },
  beta: { labelKey: "rail.labels.beta", chapter: "buddy", backingSection: "beta", target: "beta" },
  feedback: { labelKey: "rail.labels.feedback", chapter: "buddy", backingSection: "beta", target: "beta:feedback" },
  marketing_call: { labelKey: "rail.labels.marketing_call", chapter: "sales", backingSection: "marketing_call", target: "marketing_call" },
  q2: { labelKey: "rail.labels.q2", chapter: "sales", backingSection: "q2", target: "q2" },
  strategy: { labelKey: "rail.labels.strategy", chapter: "sales", backingSection: "sales", target: SALES_STEP_TARGETS.strategy },
  salespage: { labelKey: "rail.labels.salespage", chapter: "sales", backingSection: "sales", target: SALES_STEP_TARGETS.salespage },
  payments: { labelKey: "rail.labels.payments", chapter: "sales", backingSection: "sales", target: SALES_STEP_TARGETS.payments },
  app_questionnaire: { labelKey: "rail.labels.app_questionnaire", chapter: "sales", backingSection: "app", target: SALES_STEP_TARGETS.app_questionnaire },
  emails: { labelKey: "rail.labels.emails", chapter: "sales", backingSection: "sales", target: SALES_STEP_TARGETS.emails },
  webinar: { labelKey: "rail.labels.webinar", chapter: "sales", backingSection: "sales", target: SALES_STEP_TARGETS.webinar },
  stripe: { labelKey: "rail.labels.stripe", chapter: "sales", backingSection: "stripe", target: SALES_STEP_TARGETS.stripe },
  handover: { labelKey: "rail.labels.handover", chapter: "sales", backingSection: "handover", target: SALES_STEP_TARGETS.handover },
  salespage_publish: { labelKey: "rail.labels.salespage_publish", chapter: "sales", backingSection: "sales", target: SALES_STEP_TARGETS.publish },
  app_release: { labelKey: "rail.labels.app_release", chapter: "sales", backingSection: "app", target: SALES_STEP_TARGETS.release }
};

export function railTargetForStep(key: RailStepKey): RailTarget {
  return META[key].target;
}

/**
 * 🔴 F152-W2 (audit point 5): a chapter with NO steps is not rendered at all
 * (the F143 fix), so a `profile` that stayed `chapter: "buddy"` would put the
 * heading "PŘÍPRAVA BUDDYHO" back over exactly one step - the very fault F143
 * removed. In the sales_only journey the profile is not preparation of a Buddy
 * that was built elsewhere; it is the first thing the strategy and the sales
 * page get written from, so it belongs to chapter 2 with the rest.
 */
export function railChapterFor(key: RailStepKey, axes: JourneyAxes): RailChapter {
  if ((key === "profile" || key === "apple") && axes.journeyMode === "sales_only") return "sales";
  return META[key].chapter;
}

/**
 * 🔴 F152-W2 (D8): what the step's LOCK, the home's "current step" lookup and
 * the partner queue's waiting clock read behind this step.
 *
 * `META.profile.backingSection` is `q1`, and for a sales_only client that is a
 * lie in both directions: the import leaves q1 `done` and the purchase leaves it
 * `locked`, so the one step Pavel put between the video and the questionnaire
 * would inherit a state that has nothing to do with it. The step's own facts are
 * `step_confirmations.$.profile.confirmed` and the dossier - `buildRailSteps`
 * already derives its STATE from exactly those - so the section behind it is
 * `home`: the section every client owns, `done` from the moment they are
 * created, gating nothing and belonging to no chapter. The full journey keeps
 * `q1`, where the profile really is the gate in front of the Buddy
 * questionnaire.
 */
export function railBackingSection(key: RailStepKey, axes: JourneyAxes): SectionKey {
  if (key === "profile" && axes.journeyMode === "sales_only") return "home";
  return META[key].backingSection;
}

export type ClientOpenTarget = RailTarget | "account";

// F131: the surfaces a notification may lead to and the surfaces a deep link
// may open are ONE list, declared in `shared/notification-targets.ts`. Three
// copies of it used to drift: a surface could be linkable in the bell and
// stripped by the Worker's sanitizer on the way in.
const CLIENT_OPEN_TARGETS: ReadonlySet<string> = new Set(CLIENT_NOTIFICATION_SURFACES);
// Compile-time proof the two vocabularies are the same set, in both directions.
const _clientTargetParity: ClientOpenTarget = "home" as ClientNotificationSurface;
const _notificationTargetParity: ClientNotificationSurface = "home" as ClientOpenTarget;
void _clientTargetParity;
void _notificationTargetParity;

export const STEP_EYEBROW_RAIL_KEYS: Readonly<Record<string, RailStepKey>> = {
  // F135: the Apple step is a page of its own now, so it wears its own
  // position ("Krok 3 z 17") instead of the whole track's kicker.
  "app.apple.eyebrow": "apple",
  // 🔴 F152-W2 (audit drobnost 4): the profile page is a rail STEP and had no
  // entry here, so it was the one step page whose kicker could not say where it
  // sits. It matters most in the journey this wave builds, where the answer is
  // "Krok 2 z 7" - the sentence that makes Pavel's ordering visible to the
  // client. The full journey gets its own true number ("Krok 2 z 14") from the
  // same lookup.
  "profile.eyebrow": "profile",
  "sales.pages.strategy.eyebrow": "strategy",
  "sales.pages.salespage.eyebrow": "salespage",
  "sales.pages.emails.eyebrow": "emails",
  "sales.pages.webinar.eyebrow": "webinar",
  "payments.eyebrow": "payments",
  "stripe.eyebrow": "stripe",
  "app.questionnaire.eyebrow": "app_questionnaire",
  "sales.pages.publish.eyebrow": "salespage_publish",
  "handover.eyebrow": "handover",
  "app.release.eyebrow": "app_release"
};

// THE client lock gate. Everything that can put the client inside a step -
// the rail click, the workspace render, the padlock marker, the deep link from
// a notification - asks this one question, and the server asks the same one
// through `clientSectionOpen` before it releases the step's resources.
export function clientStepLocked(step: RailStep): boolean {
  return !clientSectionOpen(step.backingSection, step.state);
}

export function railInteractionForStep(step: RailStep): {
  clickable: boolean;
  target: RailTarget | null;
} {
  const clickable = !clientStepLocked(step);
  return { clickable, target: clickable ? step.target : null };
}

export function railNavigationTarget(
  _current: ClientOpenTarget,
  step: RailStep
): RailTarget | null {
  return railInteractionForStep(step).target;
}

export function clientOpenTarget(search: string): ClientOpenTarget {
  const open = new URLSearchParams(search).get("open");
  return open && CLIENT_OPEN_TARGETS.has(open as ClientOpenTarget)
    ? open as ClientOpenTarget
    : "home";
}

export function clientOpenSearch(target: ClientOpenTarget): string {
  if (target === "home") return "";
  const search = new URLSearchParams();
  search.set("open", target);
  return `?${search.toString()}`;
}

export function railStepPosition(
  steps: ReadonlyArray<RailStep>,
  key: RailStepKey
): { current: number; total: number } | null {
  const index = steps.findIndex((step) => step.key === key);
  return index < 0 ? null : { current: index + 1, total: steps.length };
}

export function buildRailSteps(
  home: HomeResponse,
  delivery?: DeliverablesResponse | null
): RailStep[] {
  const section = (key: SectionKey): SectionState =>
    home.sections.find((item) => item.key === key)?.state ?? "locked";
  // Server truth from U1c: the ordered salesSteps graph (home always carries
  // it post-merge; deliverables mirrors it). The legacy derivation below
  // stays as the fallback for cached/older payloads. Key mapping: the server
  // says publish/release, the rail speaks salespage_publish/app_release.
  const flow: Partial<Record<
    "strategy" | "salespage" | "payments" | "app_questionnaire" | "emails" | "webinar" | "stripe" | "handover" | "salespage_publish" | "app_release",
    SectionState
  >> = {};
  const serverSteps = home.salesSteps ?? delivery?.salesSteps;
  if (serverSteps) {
    for (const step of serverSteps) {
      const key = step.key === "publish" ? "salespage_publish" : step.key === "release" ? "app_release" : step.key;
      flow[key] = step.state;
    }
  }
  const appState = section("app");
  const salesState = section("sales");
  const stripeState = section("stripe");
  const includesApp = home.client.includesApp;
  const strategyApproved = newest(delivery, "plan")?.status === "approved";
  const pageApproved = newest(delivery, "salespage")?.status === "approved";
  const publication = publicationState(delivery, pageApproved, stripeState);
  const stateByKey: Record<RailStepKey, SectionState> = {
    onboarding_call: section("onboarding_call"),
    profile: home.profileConfirmed === undefined
      ? home.profile.status === "ready" ? "done" : "next"
      : home.profileConfirmed ? "done" : "next",
    apple: !includesApp
      ? "done"
      : home.appleAccountConfirmed === undefined
        ? appState === "done" ? "done" : appState === "working" ? "working" : "next"
        : home.appleAccountConfirmed ? "done" : "next",
    q1: section("q1"),
    knowhow: section("knowhow"),
    training: section("training"),
    // 🔴 F137 (Pavel 2026-08-11): "Beta se dokonci PRVNIM zkopirovanim odkazu
    // pro testery." That click is already a recorded fact - the F124
    // `beta_invite` mark, which the server derives into the beta ladder's third
    // substep - so the step closes on 3 of 3 and the client stops looking at an
    // open step they have finished.
    //
    // 🔴 It closes the STEP, not the beta SECTION, and that distinction is the
    // whole design. The section carries BOTH rail steps (F101), it is what
    // `POST /api/feedback` refuses on, and Pavel's same answer keeps the
    // feedback step running until the instance changes hands ("Zpetna vazba:
    // dokonci se predanim spravcovstvi"). A transition to `beta='done'` here
    // would have closed the feedback step on the same click AND started
    // refusing podnety - the opposite of what was asked. So the fact lands
    // where it belongs: on this step's own reading.
    //
    // Nothing locks back and nothing hides: the beta page is not in
    // `closedCardOnly` (src/App.tsx), so a done beta step renders its closed
    // BANNER above the live page, with the bot link and the tester link exactly
    // as usable as before (the F124 law, hotovo != zamek).
    beta: section("beta") === "done" || railSubstepMarked(home.stepProgress?.beta, "testers")
      ? "done"
      : section("beta"),
    feedback: section("beta") === "done"
      ? "done"
      : home.feedbackUnlocked === false
        ? "locked"
        : section("beta"),
    marketing_call: section("marketing_call"),
    q2: section("q2"),
    strategy: flow.strategy ?? deliverableState(delivery, "plan", salesState),
    salespage: flow.salespage ?? salespageStepState(strategyApproved, delivery, salesState),
    // F171: server truth only. A payload without the step never draws it.
    payments: flow.payments ?? "locked",
    app_questionnaire: flow.app_questionnaire ?? (pageApproved ? appQuestionnaireState(home) : "locked"),
    emails: flow.emails ?? gatedDeliverableState(strategyApproved, delivery, "emails", salesState),
    webinar: flow.webinar ?? gatedDeliverableState(strategyApproved, delivery, "webinar", salesState),
    stripe: flow.stripe ?? (strategyApproved ? stripeState : "locked"),
    // F101: the legacy fallback (payloads without salesSteps) follows the new
    // law too - approved materials open the handover, never the stripe step.
    handover: flow.handover ?? (materialsApproved(delivery, strategyApproved, pageApproved) ? section("handover") : "locked"),
    salespage_publish: flow.salespage_publish ?? publication,
    app_release: flow.app_release ?? appReleaseState(home, publication)
  };
  const axes = journeyAxesOf(home.client);
  const journeyKeys = railJourneyKeysFor(axes, serverSteps);
  const primaryKey = primaryRailKey(home, stateByKey, journeyKeys);
  return journeyKeys.map((key) => {
    const primaryNext = key === primaryKey;
    return {
      key,
      ...META[key],
      // F152-W2: the two facets the journey moves. Written AFTER the spread so
      // the override is the value, and read from one derivation each so the
      // client rail, the staff rail (which calls this same builder) and the
      // walkthrough cannot disagree about where the profile step belongs.
      chapter: railChapterFor(key, axes),
      backingSection: railBackingSection(key, axes),
      state: stateByKey[key],
      displayStatus: displayStatusForStep(key, stateByKey[key], primaryNext, stateByKey),
      primaryNext,
      trainingPhotoPulse: key === "training" && stateByKey.training === "working",
      profilePhotoAvailable: Boolean(home.profilePhotoUrl),
      progress: railStepProgressFor(key, stateByKey[key], home.stepProgress)
    };
  });
}

/**
 * F123: the arc belongs to a step the client is INSIDE. A locked step must not
 * advertise progress (nothing has begun) and a done step already carries the
 * green check, so the ring would only compete with it. The map itself is never
 * re-derived here - it arrives from the server.
 */
export function railStepProgressFor(
  key: RailStepKey,
  state: SectionState,
  map: RailStepProgressMap | undefined
): RailStepProgress | null {
  if (!map || state === "locked" || state === "done") return null;
  if (!(SUBSTEP_RAIL_KEYS as readonly string[]).includes(key)) return null;
  const progress = map[key as SubstepRailKey];
  return progress && progress.total > 0 ? progress : null;
}

export function nextRailStep(steps: RailStep[], current: RailStepKey | null): RailStep | null {
  const index = current ? steps.findIndex((step) => step.key === current) : -1;
  return steps[index + 1] ?? steps[0] ?? null;
}

// The server-backed primaryNext flag is the canonical answer to "what now".
// Completed pages use this helper instead of guessing that the following rail
// row is actionable: delivery work can run in parallel and app-free clients
// have a shorter rail. A stale completed primary is ignored safely.
export function currentIncompleteRailStep(steps: RailStep[]): RailStep | null {
  const available = (step: RailStep) => step.state !== "done" && step.state !== "locked";
  return steps.find((step) => step.primaryNext && available(step))
    ?? steps.find(available)
    ?? null;
}

function primaryRailKey(
  home: HomeResponse,
  states: Record<RailStepKey, SectionState>,
  journeyKeys: RailStepKey[]
): RailStepKey | null {
  const primary = home.sections.find((item) => item.primaryNext)?.key ?? null;
  if (primary && journeyKeys.includes(primary as RailStepKey)) return primary as RailStepKey;
  const virtualBySection: Partial<Record<SectionKey, RailStepKey[]>> = {
    sales: ["strategy", "salespage", "payments", "emails", "webinar", "salespage_publish"],
    app: ["apple", "app_questionnaire", "app_release"]
  };
  const isCandidate = (key: RailStepKey): boolean => {
    if (key === "strategy" && states.q2 !== "done") return false;
    return states[key] === "next" || states[key] === "working";
  };
  const preferred = (primary ? virtualBySection[primary] : undefined)?.find(isCandidate);
  if (preferred) return preferred;
  // A legacy deliverable can leave its backing section marked primary even
  // though the real client action is an earlier step. Prefer actionable
  // `next` work across the rail before background `working` states.
  return journeyKeys.find((key) => states[key] === "next" && isCandidate(key))
    ?? journeyKeys.find((key) => states[key] === "working" && isCandidate(key))
    ?? null;
}

/** The four client materials, in the DTO order the journey walks them. */
const REVIEW_MATERIAL_ORDER = ["strategy", "salespage", "emails", "webinar"] as const;

const REVIEW_STEP_KEYS = new Set<RailStepKey>(REVIEW_MATERIAL_ORDER);

/**
 * fix-homereview (Dow Janes, Pavel 2026-08-19): the first material waiting for
 * the CLIENT's review, or null when none is. The home hero card reads this to
 * stop saying "we are preparing" over a material whose ball is demonstrably
 * with the client (the sales section stays `working` forever by design, so no
 * section ever carries `primary_next` for a review).
 *
 * A rail state of `next` alone is NOT proof of a review: the server documents
 * (worker/src/sales-steps.ts) that `salespage: "next"` covers BOTH "in review"
 * and "approved, not yet published" (the F120/F137 three phases). So each
 * candidate must also show the client a reviewable document - the newest
 * published deliverable of its kind in status `review`. That same document is
 * what the step's surface renders, so the card can never point at an empty
 * door. A `next` material without one (approved salespage in its take-away
 * phases, or a failed deliverables fetch) falls through to the next candidate
 * or to the old waiting cards.
 */
export function reviewReadyStep(
  steps: ReadonlyArray<RailStep>,
  delivery: DeliverablesResponse | null | undefined
): RailStep | null {
  const kinds: Record<(typeof REVIEW_MATERIAL_ORDER)[number], DeliverableDto["kind"][]> = {
    strategy: ["plan", "audit"],
    salespage: ["salespage"],
    emails: ["emails"],
    webinar: ["webinar"]
  };
  for (const key of REVIEW_MATERIAL_ORDER) {
    const step = steps.find((item) => item.key === key);
    if (!step || step.state !== "next") continue;
    if (kinds[key].some((kind) => newest(delivery, kind)?.status === "review")) return step;
  }
  return null;
}

/**
 * F115 (audit finding 5): steps whose `working` state is the TEAM'S window,
 * never the client's. The rail used to label them "Na řadě" whenever they
 * happened to be the current candidate, and the step's own page said the
 * opposite two centimetres lower ("Nemusíš nic hlídat a nic dalšího od tebe
 * nepotřebujeme"). "Na řadě" is a call to act everywhere else in this rail, so
 * over a step with nothing to do it is simply false.
 *
 * The owner per key, each read off the step's own page:
 * - knowhow: the S27 -> S06 window while we build the folder (the law this
 *   list grew out of; it used to be the one hand-written exception below).
 * - training: Alex trains the Buddy, 2-3 working days, we write when it is
 *   done. A valid trial_link makes the PARTNER the owner (fixr5), which is
 *   still not the client.
 * - strategy / salespage / emails / webinar: `working` means the build is
 *   running or the version is not in review yet, so the client waits.
 *
 * This is the CLIENT rail only. The partner console reads its own canonical
 * truth (`staffStatus`, fixr4/fixr5) and is untouched.
 */
const TEAM_WORKING_STEP_KEYS = new Set<RailStepKey>([
  // F171: `working` = the team has access to the client's Stripe and sets up.
  "payments",
  "knowhow",
  "training",
  "strategy",
  "salespage",
  "emails",
  "webinar"
]);

export function displayStatusForStep(
  key: RailStepKey,
  state: SectionState,
  primaryNext: boolean,
  states: Record<RailStepKey, SectionState>
): RailDisplayStatus {
  if (state === "locked") return "locked";
  if (state === "done") return "done";
  if (key === "apple" && state === "working") return "waiting_apple";
  // A step the client cannot enter must not read as "Na rade" or "Na tobe".
  // A team-owned `working` step keeps the team label even when it is the only
  // candidate for the current step (F115; know-how was the first such case and
  // the only one this branch knew).
  if (TEAM_WORKING_STEP_KEYS.has(key) && state === "working") return "working";
  if (primaryNext) return "on_deck";
  if (key === "strategy" && state === "next" && states.q2 !== "done") return "review";
  if (REVIEW_STEP_KEYS.has(key) && state === "next") return "review";
  if (key === "beta" && state === "working") return "testing";
  if (key === "feedback" && state === "working") return "feedback";
  return state === "working" ? "working" : "open";
}
